import { IActionOrTriggerListPageParams } from "@app/static"
import { ParsedUrlQueryInput } from "querystring"
import { IAction } from "../entities/IAction"
import { IActionGroup } from "../entities/IActionGroup"
import { IProject } from "../entities/IProject"
import { ITrigger } from "../entities/ITrigger"
import { ITriggerGroup } from "../entities/ITriggerGroup"
import { ITriggerAction } from "../entities/ITriggerAction"
import { ITriggerActionGroup } from "../entities/ITriggerActionGroup"
import { projectStore } from "../storage/storage"
import { Log } from "@fanfanlo"

const fileLog = new Log(false, 'trigger-list-action-list-intf_f')
fileLog.pause = true
fileLog.childrenPaused = true

export enum ActionListFromEnum {
    project = 'project',
}

export enum TriggerOrActionListTypeEnum {
    addAction = 'add',
    updateAction = 'update',
}

export interface ITriggerOrActionListPageParams extends ParsedUrlQueryInput {
    backPath?: string
    type: TriggerOrActionListTypeEnum
    projectUlid: string
    triggerActionGroupUlid: string
    triggerActionUlid: string
    actionGroupUlid?: string
    triggerGroupUlid?: string
    actionUlid?: string
    triggerUlid?: string
    actionType?: string
    triggerType?: string
}

export interface ITriggerOrActionListParamsData {
    project?: IProject
    triggerActionGroup?: ITriggerActionGroup
    triggerAction?: ITriggerAction
    actionGroup?: IActionGroup
    triggerGroup?: ITriggerGroup
    action?: IAction
    trigger?: ITrigger
}

export interface ITriggerOrActionListPageParsedResult {
    params: IActionOrTriggerListPageParams
    data: ITriggerOrActionListParamsData
}

export function getTriggerOrActionListPageParams(params: IActionOrTriggerListPageParams): ITriggerOrActionListPageParsedResult {
    const fnLog = fileLog.sub(false, 'getTriggerOrActionListPageParams_fn')
    fnLog.pause = true
    fnLog.log('[getTriggerOrActionListPageParams] params=', JSON.stringify(params))

    const project = projectStore.findDraftOrSavedProject(params.projectUlid)
    const data: ITriggerOrActionListParamsData = {}
    const res = { data, params }

    if (!project) {
        fnLog.log('[getTriggerOrActionListPageParams] project not found, projectUlid=', params.projectUlid)
        return res
    }
    fnLog.log('[getTriggerOrActionListPageParams] project found, ulid=', project.ulid)
    data.project = project

    if (!project.children || project.children.length === 0) {
        fnLog.log('[getTriggerOrActionListPageParams] project.children is empty')
        return res
    }
    fnLog.log('[getTriggerOrActionListPageParams] project.children.length=', project.children.length)

    // 在 children 中查找匹配的 triggerActionGroup
    const foundTriggerActionGroup = project.children.find(
        child => child.ulid === params.triggerActionGroupUlid
    )
    if (!foundTriggerActionGroup) {
        fnLog.log('[getTriggerOrActionListPageParams] triggerActionGroup not found in children, expected=', params.triggerActionGroupUlid)
        return res
    }
    data.triggerActionGroup = foundTriggerActionGroup
    fnLog.log('[getTriggerOrActionListPageParams] triggerActionGroup found, ulid=', data.triggerActionGroup.ulid)

    if (!params.triggerActionUlid) {
        fnLog.log('[getTriggerOrActionListPageParams] triggerActionUlid is undefined')
        return res
    }

    // 注意：list 存储的是 ulid 字符串数组，children 存储的是完整对象数组
    // 所以需要在 children 中查找 triggerAction 对象
    fnLog.log('[getTriggerOrActionListPageParams] triggerActionGroup.list=', JSON.stringify(data.triggerActionGroup.list))
    fnLog.log('[getTriggerOrActionListPageParams] triggerActionGroup.children.length=', data.triggerActionGroup.children?.length)
    const triggerAction = data.triggerActionGroup.children?.find(item => item.ulid === params.triggerActionUlid)
    if (!triggerAction) {
        fnLog.log('[getTriggerOrActionListPageParams] triggerAction not found in children, ulid=', params.triggerActionUlid)
        return res
    }
    data.triggerAction = triggerAction
    fnLog.log('[getTriggerOrActionListPageParams] triggerAction found, ulid=', triggerAction.ulid)

    parseAction(data, params, triggerAction)
    parseTrigger(data, params, triggerAction)

    fnLog.log('[getTriggerOrActionListPageParams] result data=', JSON.stringify(data))
    return res
}

function parseAction(data: ITriggerOrActionListParamsData, params: IActionOrTriggerListPageParams, triggerAction: ITriggerAction) {
    const fnLog = fileLog.sub(false, 'parseAction_fn')
    fnLog.pause = true

    if (!params.actionGroupUlid) {
        fnLog.log('[parseAction] actionGroupUlid is undefined')
        return
    }
    if (!triggerAction.actionGroup) {
        fnLog.log('[parseAction] triggerAction.actionGroup is undefined')
        return
    }
    if (params.actionGroupUlid !== triggerAction.actionGroup.ulid) {
        fnLog.log('[parseAction] actionGroup ulid mismatch, expected=', params.actionGroupUlid, 'actual=', triggerAction.actionGroup.ulid)
        return
    }
    data.actionGroup = triggerAction.actionGroup
    fnLog.log('[parseAction] actionGroup found, ulid=', data.actionGroup.ulid)

    if (!params.actionUlid) {
        fnLog.log('[parseAction] actionUlid is undefined')
        return
    }
    // 修改: list 存储的是 ULID 字符串，children 存储的是完整对象
    const action = triggerAction.actionGroup.children?.find(item => item.ulid === params.actionUlid)
    if (!action) {
        fnLog.log('[parseAction] action not found in children, ulid=', params.actionUlid)
        return
    }
    data.action = action
    fnLog.log('[parseAction] action found, ulid=', action.ulid)
}

function parseTrigger(data: ITriggerOrActionListParamsData, params: IActionOrTriggerListPageParams, triggerAction: ITriggerAction) {
    const fnLog = fileLog.sub(false, 'parseTrigger_fn')
    fnLog.pause = true

    if (!params.triggerGroupUlid) {
        fnLog.log('[parseTrigger] triggerGroupUlid is undefined')
        return
    }
    if (!triggerAction.triggerGroup) {
        fnLog.log('[parseTrigger] triggerAction.triggerGroup is undefined')
        return
    }
    if (params.triggerGroupUlid !== triggerAction.triggerGroup.ulid) {
        fnLog.log('[parseTrigger] triggerGroup ulid mismatch, expected=', params.triggerGroupUlid, 'actual=', triggerAction.triggerGroup.ulid)
        return
    }
    data.triggerGroup = triggerAction.triggerGroup
    fnLog.log('[parseTrigger] triggerGroup found, ulid=', data.triggerGroup.ulid)

    if (!params.triggerUlid) {
        fnLog.log('[parseTrigger] triggerUlid is undefined')
        return
    }
    // 修改: list 存储的是 ULID 字符串，children 存储的是完整对象
    const trigger = triggerAction.triggerGroup.children?.find(item => item.ulid === params.triggerUlid)
    if (!trigger) {
        fnLog.log('[parseTrigger] trigger not found in children, ulid=', params.triggerUlid)
        return
    }
    data.trigger = trigger
    fnLog.log('[parseTrigger] trigger found, ulid=', trigger.ulid)
}
